'use client';

// Central de Notificaciones (MÓDULO 6). Bandeja de la Secretaría: lista todas las
// notificaciones del sistema (GET /notificaciones/central) con filtros por tipo y
// estado, paginación simple y acciones por fila para cambiar el estado o registrar
// la constancia. RBAC por rol de localStorage ('usuario'). Los errores 422 del
// backend llegan como { message } y se muestran como toast de error.

import { useCallback, useEffect, useState } from 'react';
import { BellRing, FileCheck2, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import {
  centralNotificaciones, radicadoDe, puedeGestionarNotificaciones, usuarioActual,
  TIPO_NOTIFICACION, ESTADO_NOTIFICACION, TIPO_LABEL, ESTADO_LABEL,
  type Notificacion,
} from '@/lib/notificaciones';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ModalCambiarEstado, ModalConstancia, badgeEstadoNotif, fmtFechaNotif,
} from './notificaciones-modales';

const TODOS = '__TODOS__';
const LIMIT = 20;

export default function CentralNotificaciones() {
  const [items, setItems] = useState<Notificacion[]>([]);
  const [meta, setMeta] = useState({ page: 1, limit: LIMIT, total: 0, totalPages: 1 });
  const [cargando, setCargando] = useState(true);
  const [tipo, setTipo] = useState<string>(TODOS);
  const [estado, setEstado] = useState<string>(TODOS);
  const [page, setPage] = useState(1);
  const [cambiar, setCambiar] = useState<Notificacion | null>(null);
  const [constancia, setConstancia] = useState<Notificacion | null>(null);

  const rol = usuarioActual()?.rol;
  const puede = puedeGestionarNotificaciones(rol);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await centralNotificaciones({
        ...(tipo !== TODOS ? { tipo } : {}),
        ...(estado !== TODOS ? { estado } : {}),
        page,
        limit: LIMIT,
      });
      setItems(res.data ?? []);
      if (res.meta) setMeta(res.meta);
    } catch (e) {
      toast.error('No se pudo cargar la central de notificaciones', {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setCargando(false);
    }
  }, [tipo, estado, page]);

  useEffect(() => { cargar(); }, [cargar]);

  // Al cambiar un filtro, volver a la primera página.
  function setTipoFiltro(v: string) { setTipo(v); setPage(1); }
  function setEstadoFiltro(v: string) { setEstado(v); setPage(1); }

  const totalPages = meta.totalPages || 1;

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex size-10 items-center justify-center rounded-lg bg-accent text-accent-foreground">
          <BellRing className="size-5" />
        </span>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Central de Notificaciones</h1>
          <p className="text-sm text-muted-foreground">
            Notificaciones y comunicaciones de todos los expedientes. La Secretaría gestiona su
            estado y registra las constancias de notificación.
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={tipo} onValueChange={setTipoFiltro}>
                <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={TODOS}>Todos</SelectItem>
                  {TIPO_NOTIFICACION.map((t) => (
                    <SelectItem key={t} value={t}>{TIPO_LABEL[t]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Estado</Label>
              <Select value={estado} onValueChange={setEstadoFiltro}>
                <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={TODOS}>Todos</SelectItem>
                  {ESTADO_NOTIFICACION.map((e) => (
                    <SelectItem key={e} value={e}>{ESTADO_LABEL[e]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={() => cargar()} disabled={cargando}>
              <RefreshCcw className="size-4" /> Actualizar
            </Button>
          </div>

          {cargando ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Cargando…</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Radicado</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Destinatario / Correo</TableHead>
                    <TableHead>Asunto</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Fecha</TableHead>
                    {puede && <TableHead className="text-right">Acciones</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((n) => (
                    <TableRow key={n.id}>
                      <TableCell className="font-medium">{radicadoDe(n)}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {TIPO_LABEL[n.tipo as keyof typeof TIPO_LABEL] ?? n.tipo}
                        </Badge>
                      </TableCell>
                      <TableCell>{n.destinatario || n.correo || '—'}</TableCell>
                      <TableCell className="max-w-[16rem] truncate">{n.asunto || '—'}</TableCell>
                      <TableCell>{badgeEstadoNotif(n.estado)}</TableCell>
                      <TableCell>{fmtFechaNotif(n.fecha_notificacion || n.fecha_envio)}</TableCell>
                      {puede && (
                        <TableCell className="text-right">
                          <div className="flex flex-wrap justify-end gap-1.5">
                            <Button size="sm" variant="outline" onClick={() => setCambiar(n)}>
                              <RefreshCcw className="size-3.5" /> Cambiar estado
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setConstancia(n)}>
                              <FileCheck2 className="size-3.5" /> Constancia
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={puede ? 7 : 6} className="py-8 text-center text-muted-foreground">
                        No hay notificaciones para los filtros seleccionados.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <div className="flex items-center justify-between pt-2">
                <p className="text-sm text-muted-foreground">
                  Página {meta.page} de {totalPages} · {meta.total} en total
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline" size="sm"
                    disabled={meta.page <= 1 || cargando}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    disabled={meta.page >= totalPages || cargando}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {cambiar && (
        <ModalCambiarEstado
          notificacion={cambiar}
          onClose={() => setCambiar(null)}
          onListo={() => { setCambiar(null); cargar(); }}
        />
      )}
      {constancia && (
        <ModalConstancia
          notificacion={constancia}
          onClose={() => setConstancia(null)}
          onListo={() => { setConstancia(null); cargar(); }}
        />
      )}
    </div>
  );
}
